import React from 'react';
import defaultAvatarUrl from '../assets/ai-advisor/02-rp-glass.svg';
import { cn } from '../lib/utils';

/**
 * Named-preset → pixel map for the AIAvatar component.
 * Exposed so consumers can reference sizes without magic numbers.
 */
export const AI_AVATAR_SIZES = {
  xs: 24,
  sm: 32,
  md: 48,
  lg: 64,
  xl: 128,
};

const ALLOWED_SIZES = new Set([24, 32, 48, 64, 128]);
const DEFAULT_SIZE = 32;

function resolveSize(size) {
  if (typeof size === 'string' && Object.prototype.hasOwnProperty.call(AI_AVATAR_SIZES, size)) {
    return AI_AVATAR_SIZES[size];
  }
  if (typeof size === 'number' && ALLOWED_SIZES.has(size)) {
    return size;
  }
  return DEFAULT_SIZE;
}

/**
 * AIAvatar — branded RendPleX AI Advisor avatar.
 *
 * Self-contained circular SVG (soft-glass RP monogram) that renders identically
 * on light and dark themes because the artwork carries its own gradient
 * background. An optional theme-aware ring can be enabled for extra separation
 * against edge-to-edge surfaces, and an optional entrance animation can be
 * toggled per placement (auto-disabled under prefers-reduced-motion via CSS).
 *
 * The underlying image source is imported once at the top of this file so a
 * future rebrand is a single-line change. Consumers can also pass `src` to
 * override the avatar per-instance without editing this component.
 *
 * Accessibility:
 *   - Pass `label` to expose the avatar as a meaningful image (role="img").
 *   - Omit `label` (or pass falsy) for decorative usage — the image is then
 *     hidden from assistive tech.
 *
 * @param {24|32|48|64|128|"xs"|"sm"|"md"|"lg"|"xl"} [size=32]  Rendered size.
 * @param {string} [label]      Accessible label. Omit for decorative usage.
 * @param {string} [src]        Optional avatar source override (for future replacement).
 * @param {boolean} [ringed]    Adds a subtle theme-aware ring around the avatar.
 * @param {boolean} [animate]   Play a subtle entrance animation on mount.
 * @param {string} [className]  Extra classes for positioning / layout.
 * @param {string} [testId]     Data-testid override.
 */
export default function AIAvatar({
  size = DEFAULT_SIZE,
  label,
  src,
  ringed = false,
  animate = false,
  className,
  testId = 'ai-avatar',
  ...rest
}) {
  const px = resolveSize(size);
  const decorative = !label;
  const source = src || defaultAvatarUrl;

  return (
    <img
      {...rest}
      src={source}
      width={px}
      height={px}
      alt={decorative ? '' : label}
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative ? 'true' : undefined}
      aria-label={decorative ? undefined : label}
      draggable={false}
      data-testid={testId}
      style={{ width: px, height: px }}
      className={cn(
        'block rounded-full select-none shrink-0',
        ringed && 'ring-1 ring-slate-200 dark:ring-slate-700',
        animate && 'rp-advisor-avatar-in',
        className
      )}
    />
  );
}
